import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { getData } from './api';

/**
 * Site-wide values the admin panel controls: contact details, social links,
 * footer text, menus and the statistics strip. Loaded once per visit.
 *
 * Every consumer must keep a sensible default, because this resolves to an
 * empty object when the backend is not reachable.
 */
const SiteContext = createContext({ settings: {}, menus: {}, stats: [], announcements: [], ready: false });

export function SiteProvider({ children }) {
  const [state, setState] = useState({ settings: {}, menus: {}, stats: [], announcements: [], ready: false });

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const data = await getData('/bootstrap');

      if (cancelled) return;

      setState({
        settings: data?.settings || {},
        menus: data?.menus || {},
        stats: data?.stats || [],
        announcements: data?.announcements || [],
        ready: true,
      });
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo(
    () => ({
      ...state,
      /** Reads a setting with a fallback, so components never render blanks. */
      setting: (key, fallback = '') => {
        const found = state.settings?.[key];
        return found === undefined || found === null || found === '' ? fallback : found;
      },
      menu: (location) => state.menus?.[location] || [],
    }),
    [state],
  );

  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
}

export function useSite() {
  return useContext(SiteContext);
}

export default SiteContext;
